import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  Skeleton,
  Breadcrumbs,
  Link,
  Alert as MuiAlert,
  Stack,
  FormControlLabel,
  Switch,
  Slider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { apiService } from '../services/api';
import { authService } from '../services/authService';
import DeviceTemperatureChart from '../components/charts/DeviceTemperatureChart';
import SensorForm, { SensorFormValues } from '../components/sensor/SensorForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import {
  Device,
  DeviceCommand,
  DeviceConfiguration,
  Actuator,
  Sensor,
  CreateActuator,
  CreateSensor,
  AlertRule,
  CreateAlertRule,
} from '../types';

const panelSx = {
  p: 2.5,
  border: 1,
  borderColor: 'divider',
  boxShadow: 'none',
  bgcolor: 'background.paper',
};

/** Aligns section title rows (device / sensors / actuators). */
const sectionHeaderSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  mb: 2,
  flexWrap: 'wrap',
  gap: 1,
  minHeight: 36,
};

/** Uniform Active/Inactive chip (device header + table Status column). */
const statusChipSx = {
  height: 24,
  minWidth: 72,
  '& .MuiChip-label': { px: 1.25, lineHeight: 1.25 },
};

/** Vertically center cell content when name/kind columns wrap to multiple lines. */
const tableAlignSx = {
  minWidth: 560,
  '& .MuiTableCell-root': {
    verticalAlign: 'middle',
  },
  '& .MuiTableCell-head': {
    verticalAlign: 'bottom',
  },
};

function sensorToFormValues(s: Sensor): SensorFormValues {
  const kind = s.signalKind ?? 'analog';
  return {
    sensorName: s.sensorName,
    sensorType: s.sensorType,
    unit: s.unit ?? '',
    edgeDeviceId: s.edgeDeviceId ?? '',
    minValue: s.minValue ?? undefined,
    maxValue: s.maxValue ?? undefined,
    signalKind: kind,
    chartStyle: kind === 'discrete' ? undefined : (s.chartStyle ?? 'line'),
    isActive: s.isActive,
  };
}

function toSensorCreatePayload(v: SensorFormValues): CreateSensor {
  const isDiscrete = v.signalKind === 'discrete';
  return {
    sensorName: v.sensorName.trim(),
    sensorType: v.sensorType.trim(),
    unit: v.unit?.trim() || undefined,
    edgeDeviceId: v.edgeDeviceId?.trim() || undefined,
    minValue: v.minValue,
    maxValue: v.maxValue,
    signalKind: v.signalKind ?? 'analog',
    chartStyle: isDiscrete ? null : (v.chartStyle ?? 'line'),
  };
}

const DeviceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [device, setDevice] = useState<Device | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [latestCommand, setLatestCommand] = useState<DeviceCommand | null>(null);
  const [commandLoading, setCommandLoading] = useState(false);
  const [commandError, setCommandError] = useState<string | null>(null);
  const [commandSuccess, setCommandSuccess] = useState<string | null>(null);
  const [powerTarget, setPowerTarget] = useState(false);
  const [analogTarget, setAnalogTarget] = useState(50);
  const [capabilityDraft, setCapabilityDraft] = useState({
    supportsTelemetry: true,
    supportsPowerControl: false,
    supportsAnalogControl: false,
    analogMin: 0,
    analogMax: 100,
    analogStep: 1,
    controlUnit: '',
  });

  const [actuators, setActuators] = useState<Actuator[]>([]);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [feedbackBySensorId, setFeedbackBySensorId] = useState<Record<number, number | undefined>>({});
  const [powerByActuatorId, setPowerByActuatorId] = useState<Record<number, boolean>>({});
  const [analogByActuatorId, setAnalogByActuatorId] = useState<Record<number, number>>({});
  const [actuatorDialogOpen, setActuatorDialogOpen] = useState(false);
  const [actuatorEditingId, setActuatorEditingId] = useState<number | null>(null);
  const [actuatorForm, setActuatorForm] = useState<CreateActuator>({
    name: '',
    description: '',
    kind: 'Discrete',
    channel: '',
    analogMin: undefined,
    analogMax: undefined,
    controlUnit: '',
    feedbackSensorId: undefined,
  });
  const [actuatorSaving, setActuatorSaving] = useState(false);
  /** Live chart dialog for one sensor */
  const [sensorChartSensorId, setSensorChartSensorId] = useState<number | null>(null);
  /** Control + feedback dialog for one actuator */
  const [actuatorControlTarget, setActuatorControlTarget] = useState<Actuator | null>(null);
  const [sensorFormOpen, setSensorFormOpen] = useState(false);
  const [editingSensor, setEditingSensor] = useState<Sensor | null>(null);
  const [feedbackLinkSavingId, setFeedbackLinkSavingId] = useState<number | null>(null);

  // Alert Rules state
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [alertRulesLoading, setAlertRulesLoading] = useState(false);
  const [arDialogOpen, setArDialogOpen] = useState(false);
  const [arEditingRule, setArEditingRule] = useState<AlertRule | null>(null);
  const [arForm, setArForm] = useState<{
    ruleName: string; ruleType: string; condition: string; severity: string;
    isEnabled: boolean; thresholdValue: string; comparisonOperator: string;
    minValue: string; maxValue: string; sensorId: number | '';
  }>({
    ruleName: '', ruleType: 'threshold', condition: '', severity: 'Medium',
    isEnabled: true, thresholdValue: '', comparisonOperator: '>', minValue: '', maxValue: '', sensorId: '',
  });
  const [arFormError, setArFormError] = useState<string | null>(null);
  const [arFormSaving, setArFormSaving] = useState(false);
  const [confirmActuator, setConfirmActuator] = useState<Actuator | null>(null);
  const [confirmSensor, setConfirmSensor] = useState<Sensor | null>(null);
  const [confirmAlertRule, setConfirmAlertRule] = useState<AlertRule | null>(null);
  const [destructiveLoading, setDestructiveLoading] = useState(false);
  const canWrite = authService.isOperatorOrAbove();

  const buildCapabilityModel = (configs: DeviceConfiguration[], deviceType?: string) => {
    const lowered = (deviceType ?? '').toLowerCase();
    const fallbackActuatorLike =
      lowered.includes('motor') || lowered.includes('actuator') || lowered.includes('controller');
    const map = new Map<string, string>();
    configs.forEach((c) => map.set(c.configurationKey, c.configurationValue ?? ''));

    const parseBool = (key: string, fallback: boolean) => {
      const value = map.get(key);
      return value == null || value === '' ? fallback : value.toLowerCase() === 'true';
    };
    const parseNumber = (key: string, fallback: number) => {
      const value = map.get(key);
      const num = Number(value);
      return Number.isFinite(num) ? num : fallback;
    };

    return {
      supportsTelemetry: parseBool('supportsTelemetry', true),
      supportsPowerControl: parseBool('supportsPowerControl', fallbackActuatorLike),
      supportsAnalogControl: parseBool('supportsAnalogControl', fallbackActuatorLike),
      analogMin: parseNumber('analogMin', 0),
      analogMax: parseNumber('analogMax', 100),
      analogStep: parseNumber('analogStep', 1),
      controlUnit: map.get('controlUnit') || '',
    };
  };

  useEffect(() => {
    if (!id) {
      setDevice(null);
      setError('Invalid device link.');
      setLoading(false);
      return;
    }
    const deviceId = Number.parseInt(id, 10);
    if (!Number.isFinite(deviceId)) {
      setDevice(null);
      setError('Invalid device ID.');
      setLoading(false);
      return;
    }
    loadDevice(deviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const refreshFeedbackForActuators = async (list: Actuator[]) => {
    const ids = Array.from(
      new Set(list.map((a) => a.feedbackSensorId).filter((x): x is number => x != null && x > 0))
    );
    if (ids.length === 0) {
      setFeedbackBySensorId({});
      return;
    }
    const next: Record<number, number | undefined> = {};
    await Promise.all(
      ids.map(async (sensorId) => {
        try {
          const page = await apiService.getSensorReadings({
            sensorId,
            pageNumber: 1,
            pageSize: 1,
          });
          next[sensorId] = page.items[0]?.value;
        } catch {
          next[sensorId] = undefined;
        }
      })
    );
    setFeedbackBySensorId(next);
  };

  const loadAlertRules = async (deviceId: number) => {
    try {
      setAlertRulesLoading(true);
      const data = await apiService.getAlertRulesByDevice(deviceId);
      setAlertRules(data);
    } catch {
      setAlertRules([]);
    } finally {
      setAlertRulesLoading(false);
    }
  };

  const loadDevice = async (deviceId: number) => {
    try {
      setLoading(true);
      setError(null);
      const [deviceData, configs, sensorList, actuatorList] = await Promise.all([
        apiService.getDevice(deviceId),
        apiService.getDeviceConfigurations(deviceId),
        apiService.getSensorsByDevice(deviceId),
        apiService.getActuatorsByDevice(deviceId),
      ]);
      const capabilities = buildCapabilityModel(configs, deviceData.deviceType);
      setDevice(deviceData);
      setSensors(sensorList);
      setActuators(actuatorList);
      void loadAlertRules(deviceId);
      setAnalogTarget(capabilities.analogMin);
      setCapabilityDraft(capabilities);

      const powerInit: Record<number, boolean> = {};
      const analogInit: Record<number, number> = {};
      actuatorList.forEach((a) => {
        powerInit[a.actuatorId] = false;
        analogInit[a.actuatorId] =
          a.kind.toLowerCase() === 'analog' ? (a.analogMin ?? 0) : 0;
      });
      setPowerByActuatorId(powerInit);
      setAnalogByActuatorId(analogInit);

      await refreshFeedbackForActuators(actuatorList);

      if (capabilities.supportsPowerControl || capabilities.supportsAnalogControl) {
        await refreshLatestCommand(deviceData.deviceId);
      } else {
        setLatestCommand(null);
      }
    } catch (err: any) {
      console.error('Error loading device:', err);
      setDevice(null);
      setActuators([]);
      setSensors([]);
      setError(err.message || 'Failed to load device');
    } finally {
      setLoading(false);
    }
  };

  const refreshLatestCommand = async (deviceId: number) => {
    try {
      const commands = await apiService.getDeviceCommands(deviceId, { pageNumber: 1, pageSize: 1 });
      setLatestCommand(commands.items.length > 0 ? commands.items[0] : null);
    } catch {
      // non-blocking for device details page
    }
  };

  const submitPowerCommand = async (on: boolean) => {
    if (!device) return;
    try {
      setCommandLoading(true);
      setCommandError(null);
      setCommandSuccess(null);
      const command = await apiService.createDeviceCommand(device.deviceId, {
        commandType: 'SetPower',
        payload: JSON.stringify({ on }),
        correlationId: `ui-power-${Date.now()}`,
      });
      setLatestCommand(command);
      setCommandSuccess(`Power command submitted (${command.status}).`);
    } catch (err: any) {
      setCommandError(err.message || 'Failed to submit power command.');
      setPowerTarget((prev) => !prev);
    } finally {
      setCommandLoading(false);
    }
  };

  const submitAnalogCommand = async () => {
    if (!device) return;
    try {
      setCommandLoading(true);
      setCommandError(null);
      setCommandSuccess(null);
      const command = await apiService.createDeviceCommand(device.deviceId, {
        commandType: 'SetValue',
        payload: JSON.stringify({ value: analogTarget }),
        correlationId: `ui-value-${Date.now()}`,
      });
      setLatestCommand(command);
      setCommandSuccess(`SetValue command submitted (${command.status}).`);
    } catch (err: any) {
      setCommandError(err.message || 'Failed to submit analog command.');
    } finally {
      setCommandLoading(false);
    }
  };

  const submitActuatorPower = async (actuator: Actuator, on: boolean) => {
    if (!device) return;
    try {
      setCommandLoading(true);
      setCommandError(null);
      setCommandSuccess(null);
      const command = await apiService.createDeviceCommand(device.deviceId, {
        commandType: 'SetPower',
        payload: JSON.stringify({ on }),
        actuatorId: actuator.actuatorId,
        correlationId: `ui-act-${actuator.actuatorId}-pwr-${Date.now()}`,
      });
      setLatestCommand(command);
      setPowerByActuatorId((p) => ({ ...p, [actuator.actuatorId]: on }));
      setCommandSuccess(`Command sent to "${actuator.name}" (${command.status}).`);
      await refreshFeedbackForActuators(actuators);
    } catch (err: any) {
      setCommandError(err.message || 'Failed to send command.');
      setPowerByActuatorId((p) => ({ ...p, [actuator.actuatorId]: !on }));
    } finally {
      setCommandLoading(false);
    }
  };

  const submitActuatorValue = async (actuator: Actuator) => {
    if (!device) return;
    const value = analogByActuatorId[actuator.actuatorId] ?? 0;
    try {
      setCommandLoading(true);
      setCommandError(null);
      setCommandSuccess(null);
      const command = await apiService.createDeviceCommand(device.deviceId, {
        commandType: 'SetValue',
        payload: JSON.stringify({ value }),
        actuatorId: actuator.actuatorId,
        correlationId: `ui-act-${actuator.actuatorId}-val-${Date.now()}`,
      });
      setLatestCommand(command);
      setCommandSuccess(`SetValue sent to "${actuator.name}" (${command.status}).`);
      await refreshFeedbackForActuators(actuators);
    } catch (err: any) {
      setCommandError(err.message || 'Failed to send command.');
    } finally {
      setCommandLoading(false);
    }
  };

  const openActuatorDialog = (mode: 'add' | 'edit', target?: Actuator) => {
    if (mode === 'add') {
      setActuatorEditingId(null);
      setActuatorForm({
        name: '',
        description: '',
        kind: 'Discrete',
        channel: '',
        analogMin: undefined,
        analogMax: undefined,
        controlUnit: '',
        feedbackSensorId: undefined,
      });
    } else if (target) {
      setActuatorEditingId(target.actuatorId);
      setActuatorForm({
        name: target.name,
        description: target.description ?? '',
        kind: target.kind,
        channel: target.channel ?? '',
        analogMin: target.analogMin,
        analogMax: target.analogMax,
        controlUnit: target.controlUnit ?? '',
        feedbackSensorId: target.feedbackSensorId,
      });
    }
    setActuatorDialogOpen(true);
  };

  const saveActuatorDialog = async () => {
    if (!device) return;
    const name = actuatorForm.name.trim();
    if (!name) {
      setCommandError('Actuator name is required.');
      return;
    }
    try {
      setActuatorSaving(true);
      setCommandError(null);
      const payload: CreateActuator = {
        name,
        description: actuatorForm.description?.trim() || undefined,
        kind: actuatorForm.kind,
        channel: actuatorForm.channel?.trim() || undefined,
        analogMin: actuatorForm.kind === 'Analog' ? actuatorForm.analogMin : undefined,
        analogMax: actuatorForm.kind === 'Analog' ? actuatorForm.analogMax : undefined,
        controlUnit: actuatorForm.kind === 'Analog' ? actuatorForm.controlUnit?.trim() || undefined : undefined,
        feedbackSensorId: actuatorForm.feedbackSensorId || undefined,
      };
      if (actuatorEditingId == null) {
        await apiService.createActuator(device.deviceId, payload);
      } else {
        await apiService.updateActuator(device.deviceId, actuatorEditingId, payload);
      }
      setActuatorDialogOpen(false);
      await loadDevice(device.deviceId);
    } catch (err: any) {
      setCommandError(err.message || 'Failed to save actuator.');
    } finally {
      setActuatorSaving(false);
    }
  };

  const deleteActuator = (actuator: Actuator) => {
    setConfirmActuator(actuator);
  };

  const doDeleteActuator = async () => {
    if (!device || !confirmActuator) return;
    setDestructiveLoading(true);
    try {
      setCommandError(null);
      await apiService.deleteActuator(device.deviceId, confirmActuator.actuatorId);
      setConfirmActuator(null);
      await loadDevice(device.deviceId);
    } catch (err: any) {
      setCommandError(err.message || 'Failed to delete actuator (commands may still reference it).');
      setConfirmActuator(null);
    } finally {
      setDestructiveLoading(false);
    }
  };

  const closeSensorForm = () => {
    setSensorFormOpen(false);
    setEditingSensor(null);
  };

  const handleSubmitSensorForm = async (values: SensorFormValues) => {
    if (!device) return;
    if (editingSensor) {
      await apiService.updateSensor(editingSensor.sensorId, {
        ...toSensorCreatePayload(values),
        isActive: values.isActive,
      });
    } else {
      await apiService.createSensor(device.deviceId, toSensorCreatePayload(values));
    }
    closeSensorForm();
    await loadDevice(device.deviceId);
  };

  const openSensorForm = (mode: 'add' | 'edit', target?: Sensor) => {
    if (mode === 'add') {
      setEditingSensor(null);
    } else if (target) {
      setEditingSensor(target);
    }
    setSensorFormOpen(true);
  };

  const deleteSensorRow = (s: Sensor) => {
    setConfirmSensor(s);
  };

  const doDeleteSensor = async () => {
    if (!device || !confirmSensor) return;
    setDestructiveLoading(true);
    try {
      setCommandError(null);
      await apiService.deleteSensor(confirmSensor.sensorId);
      if (sensorChartSensorId === confirmSensor.sensorId) setSensorChartSensorId(null);
      setConfirmSensor(null);
      await loadDevice(device.deviceId);
    } catch (err: any) {
      setCommandError(err.message || 'Failed to delete sensor.');
      setConfirmSensor(null);
    } finally {
      setDestructiveLoading(false);
    }
  };

  const doDeleteAlertRule = async () => {
    if (!device || !confirmAlertRule) return;
    setDestructiveLoading(true);
    try {
      await apiService.deleteAlertRule(confirmAlertRule.alertRuleId);
      setConfirmAlertRule(null);
      void loadAlertRules(device.deviceId);
    } catch (err: any) {
      setCommandError(err?.message || 'Failed to delete rule.');
      setConfirmAlertRule(null);
    } finally {
      setDestructiveLoading(false);
    }
  };

  const sensorLabel = (sensorId?: number) => {
    if (!sensorId) return '';
    const s = sensors.find((x) => x.sensorId === sensorId);
    return s ? `${s.sensorName} (#${sensorId})` : `#${sensorId}`;
  };

  const handleFeedbackSensorChange = async (actuator: Actuator, feedbackSensorId: number | undefined) => {
    if (!device) return;
    try {
      setCommandError(null);
      setFeedbackLinkSavingId(actuator.actuatorId);
      await apiService.updateActuator(device.deviceId, actuator.actuatorId, {
        feedbackSensorId,
      });
      await loadDevice(device.deviceId);
    } catch (err: any) {
      setCommandError(err.message || 'Failed to link feedback sensor.');
    } finally {
      setFeedbackLinkSavingId(null);
    }
  };

  const parsedId = id ? Number.parseInt(id, 10) : NaN;
  const canRetry = Number.isFinite(parsedId);
  const isControllableDevice = capabilityDraft.supportsPowerControl || capabilityDraft.supportsAnalogControl;
  const useLegacyDeviceControl = isControllableDevice && actuators.length === 0;

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={280} height={32} sx={{ mb: 2 }} />
        <Paper sx={{ ...panelSx, mb: 3 }}>
          <Skeleton variant="text" width="60%" height={48} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((k) => (
              <Grid item xs={12} sm={6} md={3} key={k}>
                <Skeleton variant="rounded" height={88} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>
    );
  }

  if (error || !device) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/devices" color="inherit" underline="hover">
            Devices
          </Link>
          <Typography color="text.primary">Detail</Typography>
        </Breadcrumbs>

        <MuiAlert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button
              color="inherit"
              size="small"
              disabled={!canRetry}
              onClick={() => canRetry && loadDevice(parsedId)}
            >
              Retry
            </Button>
          )}
        >
          {error || 'Device not found'}
        </MuiAlert>

        <Button variant="outlined" onClick={() => navigate('/devices')}>
          Back to Devices
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <ConfirmDialog
        open={confirmActuator != null}
        title="Delete Actuator"
        message={`Delete actuator "${confirmActuator?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        loading={destructiveLoading}
        onConfirm={doDeleteActuator}
        onCancel={() => setConfirmActuator(null)}
      />
      <ConfirmDialog
        open={confirmSensor != null}
        title="Delete Sensor"
        message={`Delete sensor "${confirmSensor?.sensorName}"? This cannot be undone.`}
        confirmLabel="Delete"
        loading={destructiveLoading}
        onConfirm={doDeleteSensor}
        onCancel={() => setConfirmSensor(null)}
      />
      <ConfirmDialog
        open={confirmAlertRule != null}
        title="Delete Alert Rule"
        message={`Delete rule "${confirmAlertRule?.ruleName}"? This cannot be undone.`}
        confirmLabel="Delete"
        loading={destructiveLoading}
        onConfirm={doDeleteAlertRule}
        onCancel={() => setConfirmAlertRule(null)}
      />

      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/devices" color="inherit" underline="hover">
          Devices
        </Link>
        <Typography color="text.primary">{device.deviceName}</Typography>
      </Breadcrumbs>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Operator view: each sensor opens a <strong>live chart</strong>; each actuator opens <strong>control</strong> with feedback.
        Edit definitions on the{' '}
        <Link component={RouterLink} to={`/sensors?deviceId=${device.deviceId}`} underline="hover">Sensors</Link>
        {' '}and{' '}
        <Link component={RouterLink} to={`/actuators?deviceId=${device.deviceId}`} underline="hover">Actuators</Link> tabs.
        {' '}Ask the{' '}
        <Link component={RouterLink} to={`/assistant?deviceId=${device.deviceId}`} underline="hover">Assistant</Link>
        {' '}about this device.
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper
            sx={{
              ...panelSx,
              p: 1.75,
            }}
          >
            <Box sx={sectionHeaderSx}>
              <Typography variant="h6" component="h1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                {device.deviceName}
              </Typography>
              <Chip
                label={device.isActive ? 'Active' : 'Inactive'}
                color={device.isActive ? 'success' : 'default'}
                size="small"
                sx={statusChipSx}
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr 1fr',
                  sm: 'repeat(3, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
                gap: { xs: 1.25, sm: 1.5 },
                columnGap: 2,
                rowGap: 1.25,
              }}
            >
              {[
                { label: 'Device ID', value: String(device.deviceId) },
                { label: 'Device Type', value: device.deviceType },
                ...(device.location ? [{ label: 'Location', value: device.location }] : []),
                ...(device.facilityType ? [{ label: 'Facility Type', value: device.facilityType }] : []),
                ...(device.edgeDeviceType ? [{ label: 'Edge Type', value: device.edgeDeviceType }] : []),
                ...(device.edgeDeviceId ? [{ label: 'Edge ID', value: device.edgeDeviceId }] : []),
                {
                  label: 'Last Seen',
                  value: device.lastSeenAt
                    ? new Date(device.lastSeenAt).toLocaleString()
                    : 'Never',
                },
                {
                  label: 'Created',
                  value: new Date(device.createdAt).toLocaleString(),
                },
              ].map((row) => (
                <Box key={row.label} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                    {row.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      mt: 0.25,
                      lineHeight: 1.35,
                      wordBreak: 'break-word',
                    }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={panelSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="h6" sx={{ lineHeight: 1.25 }}>Sensors (inputs)</Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button component={RouterLink} to={`/sensors?deviceId=${device.deviceId}`} variant="outlined" size="small">
                  Manage sensors
                </Button>
                {canWrite && (
                  <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => openSensorForm('add')}>
                    Add sensor
                  </Button>
                )}
              </Stack>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              One row per sensor. <strong>View</strong> opens a live chart with SignalR updates.
            </Typography>
            {sensors.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No sensors yet.{' '}
                <Link component={RouterLink} to={`/sensors?deviceId=${device.deviceId}`} underline="hover">
                  Add sensors
                </Link>
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small" sx={tableAlignSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sensor</TableCell>
                      <TableCell>Type / unit</TableCell>
                      <TableCell align="center" sx={{ width: 120 }}>Status</TableCell>
                      <TableCell align="right" sx={{ minWidth: 220 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sensors.map((s) => (
                      <TableRow key={s.sensorId} hover>
                        <TableCell>
                          <Chip label="Sensor" size="small" variant="outlined" sx={{ mr: 1, verticalAlign: 'middle' }} />
                          <Typography component="span" variant="body2" fontWeight={600}>{s.sensorName}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            ID {s.sensorId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{s.sensorType}</Typography>
                          {(s.signalKind ?? 'analog') === 'discrete' ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Discrete · LED indicator
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Analog · {(s.chartStyle ?? 'line') === 'gauge' ? 'Bar gauge' : 'Line chart'}
                            </Typography>
                          )}
                          {s.unit ? (
                            <Typography variant="caption" color="text.secondary" display="block">{s.unit}</Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                            <Chip
                              size="small"
                              label={s.isActive ? 'Active' : 'Inactive'}
                              color={s.isActive ? 'success' : 'default'}
                              sx={statusChipSx}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" flexWrap="wrap" useFlexGap>
                            {canWrite && (
                              <IconButton size="small" aria-label="edit sensor" onClick={() => openSensorForm('edit', s)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                            {canWrite && (
                              <IconButton size="small" aria-label="delete sensor" onClick={() => deleteSensorRow(s)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            )}
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setSensorChartSensorId(s.sensorId)}
                            >
                              View
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={panelSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="h6" sx={{ lineHeight: 1.25 }}>Actuators (outputs)</Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button component={RouterLink} to={`/actuators?deviceId=${device.deviceId}`} variant="outlined" size="small">
                  Manage actuators
                </Button>
                {canWrite && (
                  <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={() => openActuatorDialog('add')}>
                    Add actuator
                  </Button>
                )}
              </Stack>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Link a <strong>feedback sensor</strong> here (setting only). To see live readings and charts, use <strong>View</strong> on that sensor in Sensors above (same chart opens below).{' '}
              <strong>Control</strong> sends commands; detailed feedback still appears in Control while operating.
            </Typography>
            {actuators.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No actuators yet. Add one to target <strong>SetPower</strong> / <strong>SetValue</strong> at a specific output.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small" sx={tableAlignSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Actuator</TableCell>
                      <TableCell>Kind / channel</TableCell>
                      <TableCell sx={{ minWidth: 200, maxWidth: 280 }}>Feedback sensor</TableCell>
                      <TableCell align="center" sx={{ width: 120 }}>Status</TableCell>
                      <TableCell align="right" sx={{ minWidth: 200 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actuators.map((a) => (
                      <TableRow key={a.actuatorId} hover>
                        <TableCell>
                          <Chip label="Actuator" size="small" color="primary" variant="outlined" sx={{ mr: 1, verticalAlign: 'middle' }} />
                          <Typography component="span" variant="body2" fontWeight={600}>{a.name}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            ID {a.actuatorId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{a.kind}</Typography>
                          {a.channel ? (
                            <Typography variant="caption" color="text.secondary" display="block">{a.channel}</Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <FormControl
                            size="small"
                            fullWidth
                            disabled={feedbackLinkSavingId === a.actuatorId || sensors.length === 0}
                            sx={{ maxWidth: 260 }}
                          >
                            <InputLabel id={`fb-act-${a.actuatorId}`}>Sensor</InputLabel>
                            <Select
                              labelId={`fb-act-${a.actuatorId}`}
                              label="Sensor"
                              value={a.feedbackSensorId ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                const nextId = v === '' ? undefined : Number(v);
                                void handleFeedbackSensorChange(a, nextId);
                              }}
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {sensors.map((s) => (
                                <MenuItem key={s.sensorId} value={s.sensorId}>
                                  {s.sensorName} (ID {s.sensorId})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {sensors.length === 0 ? (
                            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                              Add sensors above first.
                            </Typography>
                          ) : a.feedbackSensorId != null ? (
                            <Box sx={{ mt: 0.75 }}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.35 }}>
                                Monitor this input under <strong>Sensors</strong> → <strong>View</strong> on that row, or:
                              </Typography>
                              <Button
                                size="small"
                                variant="text"
                                sx={{ p: 0, mt: 0.25, minWidth: 0, fontSize: '0.8125rem', textTransform: 'none' }}
                                onClick={() => setSensorChartSensorId(a.feedbackSensorId!)}
                              >
                                Open live chart (same as sensor View)
                              </Button>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Optional — then view status in Sensors above.
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                            <Chip
                              size="small"
                              label={a.isActive ? 'Active' : 'Inactive'}
                              color={a.isActive ? 'success' : 'default'}
                              sx={statusChipSx}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" flexWrap="wrap" useFlexGap>
                            {canWrite && (
                              <IconButton
                                size="small"
                                aria-label="edit actuator"
                                onClick={() => openActuatorDialog('edit', a)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                            {canWrite && (
                              <IconButton size="small" aria-label="delete actuator" onClick={() => deleteActuator(a)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            )}
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                setActuatorControlTarget(a);
                                void refreshFeedbackForActuators(actuators);
                              }}
                            >
                              Control
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Alert Rules section */}
        <Grid item xs={12}>
          <Paper sx={panelSx}>
            <Box sx={sectionHeaderSx}>
              <Typography variant="h6" sx={{ lineHeight: 1.25 }}>Alert Rules</Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  component={RouterLink}
                  to={`/alert-rules?deviceId=${device.deviceId}`}
                  variant="outlined"
                  size="small"
                >
                  Manage all rules
                </Button>
                {canWrite && (
                  <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    size="small"
                    onClick={() => {
                      setArEditingRule(null);
                      setArForm({ ruleName: '', ruleType: 'threshold', condition: '', severity: 'Medium', isEnabled: true, thresholdValue: '', comparisonOperator: '>', minValue: '', maxValue: '', sensorId: '' });
                      setArFormError(null);
                      setArDialogOpen(true);
                    }}
                  >
                    Add rule
                  </Button>
                )}
              </Stack>
            </Box>
            {alertRulesLoading ? (
              <Stack spacing={1}>
                {[1, 2].map((k) => <Skeleton key={k} variant="rounded" height={44} />)}
              </Stack>
            ) : alertRules.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No alert rules for this device. Add one to start monitoring thresholds.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rule Name</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Condition</TableCell>
                      <TableCell>Sensor</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alertRules.map((rule) => {
                      const sensor = sensors.find((s) => s.sensorId === rule.sensorId);
                      const condSummary = rule.ruleType.toLowerCase() === 'threshold'
                        ? `Value ${rule.comparisonOperator ?? '>'} ${rule.thresholdValue ?? '—'}`
                        : rule.ruleType.toLowerCase() === 'range'
                        ? `${rule.minValue ?? '—'} to ${rule.maxValue ?? '—'}`
                        : 'Any state change';
                      return (
                        <TableRow key={rule.alertRuleId} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{rule.ruleName}</Typography>
                            <Typography variant="caption" color="text.secondary">{rule.condition}</Typography>
                          </TableCell>
                          <TableCell><Chip label={rule.ruleType} size="small" variant="outlined" /></TableCell>
                          <TableCell><Typography variant="body2">{condSummary}</Typography></TableCell>
                          <TableCell>
                            <Typography variant="body2">{sensor ? sensor.sensorName : rule.sensorId ? `Sensor ${rule.sensorId}` : 'All sensors'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={rule.severity}
                              color={rule.severity.toLowerCase() === 'critical' ? 'error' : rule.severity.toLowerCase() === 'high' ? 'warning' : rule.severity.toLowerCase() === 'medium' ? 'info' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label={rule.isEnabled ? 'Enabled' : 'Disabled'} color={rule.isEnabled ? 'success' : 'default'} size="small" variant={rule.isEnabled ? 'filled' : 'outlined'} />
                          </TableCell>
                          <TableCell align="right">
                            {canWrite && (
                              <IconButton size="small" title="Edit" onClick={() => {
                                setArEditingRule(rule);
                                setArForm({
                                  ruleName: rule.ruleName,
                                  ruleType: rule.ruleType.toLowerCase(),
                                  condition: rule.condition,
                                  severity: rule.severity,
                                  isEnabled: rule.isEnabled,
                                  thresholdValue: rule.thresholdValue != null ? String(rule.thresholdValue) : '',
                                  comparisonOperator: rule.comparisonOperator ?? '>',
                                  minValue: rule.minValue != null ? String(rule.minValue) : '',
                                  maxValue: rule.maxValue != null ? String(rule.maxValue) : '',
                                  sensorId: rule.sensorId ?? '',
                                });
                                setArFormError(null);
                                setArDialogOpen(true);
                              }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                            {canWrite && (
                              <IconButton size="small" title="Delete" color="error" onClick={() => setConfirmAlertRule(rule)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Alert Rule Add/Edit Dialog */}
        <Dialog open={arDialogOpen} onClose={() => setArDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{arEditingRule ? 'Edit Alert Rule' : 'Add Alert Rule'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {arFormError && <MuiAlert severity="error">{arFormError}</MuiAlert>}
              <TextField label="Rule Name" required fullWidth value={arForm.ruleName} onChange={(e) => setArForm((f) => ({ ...f, ruleName: e.target.value }))} />
              <FormControl fullWidth required>
                <InputLabel>Rule Type</InputLabel>
                <Select value={arForm.ruleType} label="Rule Type" onChange={(e) => setArForm((f) => ({ ...f, ruleType: e.target.value }))}>
                  {['threshold', 'range', 'change'].map((t) => <MenuItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</MenuItem>)}
                </Select>
                <FormHelperText>
                  {arForm.ruleType === 'threshold' && 'Fires when value crosses a single threshold.'}
                  {arForm.ruleType === 'range' && 'Fires when value goes outside the min–max range.'}
                  {arForm.ruleType === 'change' && 'Fires when the sensor value changes from its previous reading.'}
                </FormHelperText>
              </FormControl>
              {arForm.ruleType === 'threshold' && (
                <Stack direction="row" spacing={2}>
                  <FormControl sx={{ minWidth: 120 }} required>
                    <InputLabel>Operator</InputLabel>
                    <Select value={arForm.comparisonOperator} label="Operator" onChange={(e) => setArForm((f) => ({ ...f, comparisonOperator: e.target.value }))}>
                      {['>', '>=', '<', '<=', '==', '!='].map((op) => <MenuItem key={op} value={op}>{op}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField label="Threshold Value" type="number" required fullWidth value={arForm.thresholdValue} onChange={(e) => setArForm((f) => ({ ...f, thresholdValue: e.target.value }))} />
                </Stack>
              )}
              {arForm.ruleType === 'range' && (
                <Stack direction="row" spacing={2}>
                  <TextField label="Min Value" type="number" required fullWidth value={arForm.minValue} onChange={(e) => setArForm((f) => ({ ...f, minValue: e.target.value }))} helperText="Alert if value goes below this" />
                  <TextField label="Max Value" type="number" required fullWidth value={arForm.maxValue} onChange={(e) => setArForm((f) => ({ ...f, maxValue: e.target.value }))} helperText="Alert if value goes above this" />
                </Stack>
              )}
              <FormControl fullWidth>
                <InputLabel>Sensor (optional)</InputLabel>
                <Select value={arForm.sensorId} label="Sensor (optional)" onChange={(e) => setArForm((f) => ({ ...f, sensorId: e.target.value as number | '' }))}>
                  <MenuItem value="">All sensors on this device</MenuItem>
                  {sensors.map((s) => <MenuItem key={s.sensorId} value={s.sensorId}>{s.sensorName} ({s.sensorType}{s.unit ? `, ${s.unit}` : ''})</MenuItem>)}
                </Select>
                <FormHelperText>Leave blank to apply to all sensors on this device.</FormHelperText>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Severity</InputLabel>
                <Select value={arForm.severity} label="Severity" onChange={(e) => setArForm((f) => ({ ...f, severity: e.target.value }))}>
                  {['Low', 'Medium', 'High', 'Critical'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Condition / Alert Message" required fullWidth multiline rows={2} value={arForm.condition} onChange={(e) => setArForm((f) => ({ ...f, condition: e.target.value }))} helperText="This message appears on the alert when it fires." />
              <FormControlLabel control={<Switch checked={arForm.isEnabled} onChange={(e) => setArForm((f) => ({ ...f, isEnabled: e.target.checked }))} />} label="Enabled" />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArDialogOpen(false)} disabled={arFormSaving}>Cancel</Button>
            <Button variant="contained" disabled={arFormSaving} onClick={async () => {
              if (!arForm.ruleName.trim()) { setArFormError('Rule Name is required.'); return; }
              if (!arForm.condition.trim()) { setArFormError('Condition / Message is required.'); return; }
              if (arForm.ruleType === 'threshold' && arForm.thresholdValue === '') { setArFormError('Threshold Value is required.'); return; }
              if (arForm.ruleType === 'range' && (arForm.minValue === '' || arForm.maxValue === '')) { setArFormError('Min and Max Value are required.'); return; }
              setArFormSaving(true);
              setArFormError(null);
              try {
                const payload: CreateAlertRule = {
                  deviceId: device.deviceId,
                  sensorId: arForm.sensorId !== '' ? arForm.sensorId : undefined,
                  ruleName: arForm.ruleName.trim(),
                  ruleType: arForm.ruleType,
                  condition: arForm.condition.trim(),
                  severity: arForm.severity,
                  isEnabled: arForm.isEnabled,
                  ...(arForm.ruleType === 'threshold' && { thresholdValue: Number(arForm.thresholdValue), comparisonOperator: arForm.comparisonOperator }),
                  ...(arForm.ruleType === 'range' && { minValue: Number(arForm.minValue), maxValue: Number(arForm.maxValue) }),
                };
                if (arEditingRule) {
                  await apiService.updateAlertRule(arEditingRule.alertRuleId, payload);
                } else {
                  await apiService.createAlertRule(payload);
                }
                setArDialogOpen(false);
                void loadAlertRules(device.deviceId);
              } catch (err: any) {
                setArFormError(err?.message || 'Failed to save rule.');
              } finally {
                setArFormSaving(false);
              }
            }}>
              {arFormSaving ? 'Saving…' : arEditingRule ? 'Save Changes' : 'Add Rule'}
            </Button>
          </DialogActions>
        </Dialog>

        {isControllableDevice && (
          <Grid item xs={12}>
            <Paper sx={panelSx}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Command status
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Latest command for this device (including per-actuator commands). Refresh after MQTT acknowledgment if needed.
              </Typography>
              {commandError && <MuiAlert severity="error" sx={{ mb: 2 }}>{commandError}</MuiAlert>}
              {commandSuccess && <MuiAlert severity="success" sx={{ mb: 2 }}>{commandSuccess}</MuiAlert>}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">Latest command:</Typography>
                {latestCommand ? (
                  <>
                    <Chip size="small" label={`#${latestCommand.commandId}`} />
                    <Chip size="small" label={latestCommand.commandType} />
                    {latestCommand.actuatorId != null && (
                      <Chip size="small" variant="outlined" label={`Actuator ${latestCommand.actuatorId}`} />
                    )}
                    <Chip
                      size="small"
                      color={latestCommand.status === 'Failed' ? 'error' : latestCommand.status === 'Acked' ? 'success' : 'info'}
                      label={latestCommand.status}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(latestCommand.createdAt).toLocaleString()}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2">No commands yet</Typography>
                )}
                <Button size="small" onClick={() => refreshLatestCommand(device.deviceId)} disabled={commandLoading}>
                  Refresh status
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}

        {useLegacyDeviceControl && (
          <Grid item xs={12}>
            <Paper sx={panelSx}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Device Control (legacy)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Submit control commands without per-actuator targeting. Add actuators above to hide this section.
              </Typography>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
                {capabilityDraft.supportsPowerControl && (
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={powerTarget}
                        disabled={commandLoading}
                        onChange={(event) => {
                          const next = event.target.checked;
                          setPowerTarget(next);
                          submitPowerCommand(next);
                        }}
                      />
                    )}
                    label={powerTarget ? 'Power target: ON' : 'Power target: OFF'}
                  />
                )}

                {capabilityDraft.supportsAnalogControl && (
                  <>
                    <Box sx={{ minWidth: 260, flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Analog target value: {analogTarget}{capabilityDraft.controlUnit ? ` ${capabilityDraft.controlUnit}` : ''}
                      </Typography>
                      <Slider
                        value={analogTarget}
                        min={capabilityDraft.analogMin}
                        max={capabilityDraft.analogMax}
                        step={capabilityDraft.analogStep}
                        disabled={commandLoading}
                        onChange={(_, value) => setAnalogTarget(value as number)}
                      />
                    </Box>

                    <TextField
                      label="Value"
                      type="number"
                      size="small"
                      value={analogTarget}
                      onChange={(event) => setAnalogTarget(Number(event.target.value))}
                      sx={{ width: 140 }}
                      inputProps={{ min: capabilityDraft.analogMin, max: capabilityDraft.analogMax, step: capabilityDraft.analogStep }}
                      disabled={commandLoading}
                    />

                    <Button variant="contained" onClick={submitAnalogCommand} disabled={commandLoading}>
                      Send SetValue
                    </Button>
                  </>
                )}
              </Stack>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={sensorChartSensorId != null}
        onClose={() => setSensorChartSensorId(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Live readings —{' '}
          {sensorChartSensorId != null
            ? sensors.find((x) => x.sensorId === sensorChartSensorId)?.sensorName ?? `Sensor ${sensorChartSensorId}`
            : ''}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {sensorChartSensorId != null && (
            <DeviceTemperatureChart
              key={sensorChartSensorId}
              deviceId={device.deviceId}
              deviceName={device.deviceName}
              sensorId={sensorChartSensorId}
              height={420}
              showPaper={false}
              windowMode="time"
              timeWindowMinutes={5}
              hideDiscreteSensorHeading
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSensorChartSensorId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={actuatorControlTarget != null}
        onClose={() => setActuatorControlTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Control — {actuatorControlTarget?.name ?? ''}
        </DialogTitle>
        <DialogContent dividers>
          {actuatorControlTarget && (
            <>
              {!actuatorControlTarget.isActive && (
                <MuiAlert severity="warning" sx={{ mb: 2 }}>
                  This actuator is inactive. Turn it on from the Actuators tab or edit dialog before sending commands.
                </MuiAlert>
              )}
              {commandError && <MuiAlert severity="error" sx={{ mb: 2 }} onClose={() => setCommandError(null)}>{commandError}</MuiAlert>}
              {commandSuccess && <MuiAlert severity="success" sx={{ mb: 2 }} onClose={() => setCommandSuccess(null)}>{commandSuccess}</MuiAlert>}

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {actuatorControlTarget.kind}
                {actuatorControlTarget.channel ? ` • ${actuatorControlTarget.channel}` : ''}
              </Typography>

              {actuatorControlTarget.feedbackSensorId != null && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Feedback
                  </Typography>
                  <Typography variant="body2">
                    {sensorLabel(actuatorControlTarget.feedbackSensorId)} —{' '}
                    <strong>
                      {feedbackBySensorId[actuatorControlTarget.feedbackSensorId] === undefined
                        ? '—'
                        : actuatorControlTarget.kind.toLowerCase() === 'discrete'
                          ? (feedbackBySensorId[actuatorControlTarget.feedbackSensorId] ?? 0) >= 1
                            ? 'ON'
                            : 'OFF'
                          : String(feedbackBySensorId[actuatorControlTarget.feedbackSensorId])}
                      {actuatorControlTarget.kind.toLowerCase() === 'analog' && actuatorControlTarget.controlUnit
                        ? ` ${actuatorControlTarget.controlUnit}`
                        : ''}
                    </strong>
                  </Typography>
                  <Button size="small" sx={{ mt: 1 }} onClick={() => refreshFeedbackForActuators(actuators)} disabled={commandLoading}>
                    Refresh feedback
                  </Button>
                </Box>
              )}

              {actuatorControlTarget.kind.toLowerCase() === 'discrete' && (
                <FormControlLabel
                  sx={{ mt: 1, display: 'block' }}
                  control={(
                    <Switch
                      checked={powerByActuatorId[actuatorControlTarget.actuatorId] ?? false}
                      disabled={commandLoading || !actuatorControlTarget.isActive}
                      onChange={(_, v) => {
                        setPowerByActuatorId((p) => ({ ...p, [actuatorControlTarget.actuatorId]: v }));
                        void submitActuatorPower(actuatorControlTarget, v);
                      }}
                    />
                  )}
                  label={powerByActuatorId[actuatorControlTarget.actuatorId] ? 'Target ON' : 'Target OFF'}
                />
              )}

              {actuatorControlTarget.kind.toLowerCase() === 'analog' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 2 }}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="caption" color="text.secondary">
                      Value ({actuatorControlTarget.controlUnit || 'units'})
                    </Typography>
                    <Slider
                      value={analogByActuatorId[actuatorControlTarget.actuatorId] ?? (actuatorControlTarget.analogMin ?? 0)}
                      min={actuatorControlTarget.analogMin ?? 0}
                      max={actuatorControlTarget.analogMax ?? 100}
                      step={0.25}
                      disabled={commandLoading || !actuatorControlTarget.isActive}
                      onChange={(_, v) => setAnalogByActuatorId((p) => ({ ...p, [actuatorControlTarget.actuatorId]: v as number }))}
                    />
                  </Box>
                  <TextField
                    size="small"
                    type="number"
                    label="Value"
                    value={analogByActuatorId[actuatorControlTarget.actuatorId] ?? ''}
                    onChange={(e) => setAnalogByActuatorId((p) => ({
                      ...p,
                      [actuatorControlTarget.actuatorId]: Number(e.target.value),
                    }))}
                    InputProps={{ inputProps: { min: actuatorControlTarget.analogMin, max: actuatorControlTarget.analogMax, step: 0.25 } }}
                    sx={{ width: 120 }}
                    disabled={commandLoading || !actuatorControlTarget.isActive}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => submitActuatorValue(actuatorControlTarget)}
                    disabled={commandLoading || !actuatorControlTarget.isActive}
                  >
                    Send SetValue
                  </Button>
                </Stack>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActuatorControlTarget(null)}>Close</Button>
          <Button
            size="small"
            onClick={() => refreshLatestCommand(device.deviceId)}
            disabled={commandLoading}
          >
            Refresh command status
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={actuatorDialogOpen} onClose={() => setActuatorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{actuatorEditingId == null ? 'Add actuator' : 'Edit actuator'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              required
              fullWidth
              value={actuatorForm.name}
              onChange={(e) => setActuatorForm((f) => ({ ...f, name: e.target.value }))}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={actuatorForm.description ?? ''}
              onChange={(e) => setActuatorForm((f) => ({ ...f, description: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel id="act-kind-label">Kind</InputLabel>
              <Select
                labelId="act-kind-label"
                label="Kind"
                value={actuatorForm.kind}
                onChange={(e) => setActuatorForm((f) => ({ ...f, kind: String(e.target.value) }))}
              >
                <MenuItem value="Discrete">Discrete (SetPower / on-off)</MenuItem>
                <MenuItem value="Analog">Analog (SetValue)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Channel (optional, for firmware routing)"
              fullWidth
              value={actuatorForm.channel ?? ''}
              onChange={(e) => setActuatorForm((f) => ({ ...f, channel: e.target.value }))}
            />
            {actuatorForm.kind === 'Analog' && (
              <>
                <TextField
                  label="Analog min"
                  type="number"
                  fullWidth
                  value={actuatorForm.analogMin ?? ''}
                  onChange={(e) =>
                    setActuatorForm((f) => ({
                      ...f,
                      analogMin: e.target.value === '' ? undefined : Number(e.target.value),
                    }))
                  }
                />
                <TextField
                  label="Analog max"
                  type="number"
                  fullWidth
                  value={actuatorForm.analogMax ?? ''}
                  onChange={(e) =>
                    setActuatorForm((f) => ({
                      ...f,
                      analogMax: e.target.value === '' ? undefined : Number(e.target.value),
                    }))
                  }
                />
                <TextField
                  label="Control unit"
                  fullWidth
                  value={actuatorForm.controlUnit ?? ''}
                  onChange={(e) => setActuatorForm((f) => ({ ...f, controlUnit: e.target.value }))}
                />
              </>
            )}
            <FormControl fullWidth>
              <InputLabel id="act-fb-label">Feedback sensor</InputLabel>
              <Select
                labelId="act-fb-label"
                label="Feedback sensor"
                value={actuatorForm.feedbackSensorId ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setActuatorForm((f) => ({
                    ...f,
                    feedbackSensorId: v === '' ? undefined : Number(v),
                  }));
                }}
              >
                <MenuItem value="">None</MenuItem>
                {sensors.map((s) => (
                  <MenuItem key={s.sensorId} value={s.sensorId}>
                    {s.sensorName} (#{s.sensorId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActuatorDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void saveActuatorDialog()} disabled={actuatorSaving}>
            {actuatorSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <SensorForm
        key={editingSensor ? `edit-${editingSensor.sensorId}` : 'create'}
        open={sensorFormOpen}
        onClose={closeSensorForm}
        onSubmit={handleSubmitSensorForm}
        initialData={editingSensor ? sensorToFormValues(editingSensor) : undefined}
        title={editingSensor ? 'Edit sensor' : 'Add sensor'}
        isEdit={Boolean(editingSensor)}
      />
    </Box>
  );
};

export default DeviceDetailPage;
